import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import menuService from '../services/menuService'
import aiImageService from '../services/aiImageService'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { Menu, MenuItem, MenuDish } from '../types/menu'
import CreateMenuWithAIModal from '../components/CreateMenuWithAIModal'
import LogFromMenuModal from '../components/LogFromMenuModal'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'

// Mock ingredient metrics (replace with API when backend has cost/loss over time)
function useIngredientMetrics(ingredientId: string | undefined) {
  const [metrics] = useState(() => {
    if (!ingredientId) return null
    const seed = ingredientId.split('').reduce((s, c) => s + c.charCodeAt(0), 0)
    const r = (n: number) => ((seed * n) % 100) / 100
    return {
      last1Day: { kg: Math.round((0.5 + r(1)) * 100) / 100, percent: Math.round((10 + r(2) * 30)) },
      last7Days: { kg: Math.round((3 + r(3) * 5) * 100) / 100, percent: Math.round((50 + r(4) * 40)) },
      last30Days: { kg: Math.round((12 + r(5) * 15) * 100) / 100, percent: Math.round((80 + r(6) * 18)) },
    }
  })
  return metrics
}

function IngredientMetricsRow({ ingredientId, ingredientName }: { ingredientId?: string; ingredientName: string }) {
  const metrics = useIngredientMetrics(ingredientId)
  if (!metrics) {
    return (
      <tr className="border-t border-white/10">
        <td className="py-2 px-3 text-white/90">{ingredientName}</td>
        <td className="py-2 px-3 text-right text-white/70">—</td>
        <td className="py-2 px-3 text-right text-white/70">—</td>
        <td className="py-2 px-3 text-right text-white/70">—</td>
        <td className="py-2 px-3 text-right text-white/70">—</td>
        <td className="py-2 px-3 text-right text-white/70">—</td>
      </tr>
    )
  }
  return (
    <tr className="border-t border-white/10">
      <td className="py-2 px-3 text-white/90">{ingredientName}</td>
      <td className="py-2 px-3 text-right text-white/90">{metrics.last1Day.kg} kg</td>
      <td className="py-2 px-3 text-right text-white/90">{metrics.last1Day.percent}%</td>
      <td className="py-2 px-3 text-right text-white/90">{metrics.last7Days.kg} kg</td>
      <td className="py-2 px-3 text-right text-white/90">{metrics.last7Days.percent}%</td>
      <td className="py-2 px-3 text-right text-white/90">{metrics.last30Days.kg} kg</td>
      <td className="py-2 px-3 text-right text-white/90">{metrics.last30Days.percent}%</td>
    </tr>
  )
}

const MenuDetail: React.FC = () => {
  const { t } = useTranslation()
  const { menuId } = useParams<{ menuId: string }>()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [menu, setMenu] = useState<Menu | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAiPicturesModal, setShowAiPicturesModal] = useState(false)
  const [logMenuItemId, setLogMenuItemId] = useState<string | null>(null)

  useEffect(() => {
    const fetchMenu = async () => {
      if (!menuId) {
        setError(t('error.menuIdMissing'))
        setIsLoading(false)
        return
      }
      try {
        setIsLoading(true)
        const data = await menuService.getMenu(menuId)
        setMenu(data)
      } catch (err: any) {
        console.error('Failed to fetch menu:', err)
        setError(err.response?.data?.message || t('toast.menuLoadFailed'))
        toast.error(t('toast.menuLoadFailed'))
      } finally {
        setIsLoading(false)
      }
    }
    fetchMenu()
  }, [menuId])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  if (error || !menu) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('menu.notFound.title')}</h2>
          <p className="text-white/80 mb-6">{error || t('menu.notFound.defaultMessage')}</p>
          <Link
            to="/dashboard"
            className="inline-block bg-black hover:bg-black/80 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300"
          >
            {t('menu.notFound.backToDashboard')}
          </Link>
        </div>
      </div>
    )
  }

  const restaurantId = menu.restaurantId || menu.restaurant?.id || menu.restaurant?._id
  const isOwner = user?._id === menu.owner?._id || user?._id === menu.owner?.id

  const handleDeleteMenu = async () => {
    if (!menuId || !window.confirm(t('menu.list.deleteConfirm'))) return
    try {
      await menuService.deleteMenu(menuId)
      toast.success(t('toast.menuDeleted'))
      navigate(restaurantId ? `/restaurant/${restaurantId}` : '/dashboard')
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.menuDeleteFailed'))
    }
  }

  const isCustomMenu =
    menu.type === 'custom' || (Array.isArray(menu.dishes) && menu.dishes.length > 0)
  const hasItems = (menu.items && menu.items.length > 0) || false

  if (isCustomMenu) {
    return (
      <div className="min-h-screen">
        <main className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
          <Link
            to={restaurantId ? `/restaurant/${restaurantId}` : '/dashboard'}
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            {restaurantId ? t('menu.detail.backToRestaurant') : t('menu.detail.backToDashboard')}
          </Link>

          <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">{menu.name}</h1>
                {menu.description && (
                  <p className="text-white/70 text-sm sm:text-base">{menu.description}</p>
                )}
              </div>
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-500/80 text-white">
                {t('menu.detail.customBadge')}
              </span>
              {isOwner && (
                <button type="button" onClick={handleDeleteMenu} className="text-red-400 text-sm font-semibold">
                  {t('menu.list.delete')}
                </button>
              )}
            </div>

            <div className="space-y-8">
              {(!menu.dishes || menu.dishes.length === 0) ? (
                <p className="text-white/60">{t('menu.detail.noDishes')}</p>
              ) : (
              (menu.dishes as MenuDish[]).map((dish) => (
                <section key={dish.id} className="border border-white/10 rounded-xl p-5">
                  <h2 className="text-lg font-semibold text-white mb-3">
                    {dish.name}
                    {dish.region ? (
                      <span className="text-white/60 font-normal ml-2">({dish.region})</span>
                    ) : null}
                  </h2>
                  {dish.ingredients && dish.ingredients.length > 0 ? (
                    <>
                      <p className="text-white/70 text-sm mb-3">{t('menu.detail.ingredientsTitle')}</p>
                      <ul className="list-disc list-inside text-white/80 text-sm mb-4">
                        {dish.ingredients.map((ing) => (
                          <li key={ing.id || ing.name}>{ing.name}</li>
                        ))}
                      </ul>
                      <p className="text-white/70 text-sm mb-2">
                        {t('menu.detail.costAcquisitionHeader')}
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-white/10 rounded-lg">
                          <thead>
                            <tr className="bg-white/10">
                              <th className="text-white/80 font-semibold py-2 px-3 text-left">
                                {t('cost.table.ingredient')}
                              </th>
                              <th className="text-white/80 font-semibold py-2 px-3 text-right">
                                {t('common.last1DayKg')}
                              </th>
                              <th className="text-white/80 font-semibold py-2 px-3 text-right">
                                {t('common.last1DayPct')}
                              </th>
                              <th className="text-white/80 font-semibold py-2 px-3 text-right">
                                {t('common.last7DaysKg')}
                              </th>
                              <th className="text-white/80 font-semibold py-2 px-3 text-right">
                                {t('common.last7DaysPct')}
                              </th>
                              <th className="text-white/80 font-semibold py-2 px-3 text-right">
                                {t('common.last30DaysKg')}
                              </th>
                              <th className="text-white/80 font-semibold py-2 px-3 text-right">
                                {t('common.last30DaysPct')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {dish.ingredients.map((ing) => (
                              <IngredientMetricsRow
                                key={ing.id || ing.name}
                                ingredientId={ing.id}
                                ingredientName={ing.name}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className="text-white/60 text-sm">{t('common.noIngredientsData')}</p>
                  )}
                </section>
              ))
              )}
            </div>
          </div>
        </main>
      </div>
    )
  }

  // Generated menu: items by category
  const itemsByCategory = (menu.items || []).reduce<Record<string, MenuItem[]>>((acc, item) => {
    const cat = item.category || t('common.other')
    let arr = acc[cat]
    if (!arr) {
      arr = []
      acc[cat] = arr
    }
    arr.push(item)
    return acc
  }, {})
  const categories = Object.keys(itemsByCategory).sort()

  return (
    <div className="min-h-screen">
      <main className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <Link
          to={restaurantId ? `/restaurant/${restaurantId}` : '/dashboard'}
          className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
        >
          {restaurantId ? t('menu.detail.backToRestaurant') : t('menu.detail.backToDashboard')}
        </Link>

        <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">{menu.name}</h1>
              {menu.description && (
                <p className="text-white/70 text-sm sm:text-base">{menu.description}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {restaurantId && hasItems && (
                <button
                  type="button"
                  onClick={() => setShowAiPicturesModal(true)}
                  className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-medium transition-colors"
                >
                  {t('menu.detail.generatePictures')}
                </button>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                menu.status === 'active'
                  ? 'bg-green-500 text-white'
                  : menu.status === 'draft'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-500 text-white'
              }`}>
                {menu.status || t('common.status.draft')}
              </span>
            </div>
          </div>

          <div className="space-y-8">
            {categories.length === 0 ? (
              <p className="text-white/60">{t('menu.detail.noItems')}</p>
            ) : (
              categories.map((category) => (
                <section key={category}>
                  <h2 className="text-lg font-semibold text-white border-b border-white/20 pb-2 mb-4">
                    {category}
                  </h2>
                  <ul className="space-y-4">
                    {(itemsByCategory[category] || []).map((item) => {
                      const imageUrl = item.aiGeneratedImageId
                        ? aiImageService.getAiImageUrl(item.aiGeneratedImageId)
                        : item.imageUrl
                      return (
                        <li
                          key={item.id || item._id || item.name}
                          className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 py-3 border-b border-white/5 last:border-0"
                        >
                          <div className="flex gap-3 flex-1 min-w-0">
                            {imageUrl && (
                              <img
                                src={imageUrl}
                                alt={item.name}
                                className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white">{item.name}</p>
                              {item.description && (
                                <p className="text-sm text-white/70 mt-1">{item.description}</p>
                              )}
                              {(item.id || item._id) && (
                                <button
                                  type="button"
                                  onClick={() => setLogMenuItemId(String(item.id || item._id))}
                                  className="mt-2 text-emerald-400 text-sm font-medium hover:text-emerald-300"
                                >
                                  {t('menu.logToHowIEat')}
                                </button>
                              )}
                            </div>
                          </div>
                          {item.price != null && !Number.isNaN(Number(item.price)) && (
                            <span className="text-white font-semibold whitespace-nowrap">
                              ${Number(item.price).toFixed(2)}
                            </span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ))
            )}
          </div>
        </div>

        {showAiPicturesModal && restaurantId && menuId && (
          <CreateMenuWithAIModal
            restaurantId={restaurantId}
            initialMenuId={menuId}
            onClose={() => setShowAiPicturesModal(false)}
            onSuccess={() => {
              setShowAiPicturesModal(false)
              menuService.getMenu(menuId).then(setMenu).catch(() => {})
            }}
          />
        )}
        {logMenuItemId && (
          <LogFromMenuModal
            menuItemId={logMenuItemId}
            onClose={() => setLogMenuItemId(null)}
            onSuccess={() => toast.success(t('toast.loggedToHowIEat'))}
          />
        )}
      </main>
    </div>
  )
}

export default MenuDetail
